import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setSelect, setViewMode, setColorScheme } from "../../../features/users/userSlice"
import { useAuth0 } from "@auth0/auth0-react"
import { getThemeColors } from "../../../utils/themeColors"
import useLogging from "../../../hooks/useLogging"

const CalendarSettings = ({ modalOpen, setModalOpen }) => {
	const { getAccessTokenSilently } = useAuth0()
	const dispatch = useDispatch()
	const { select, viewMode, colorScheme, columnCount } = useSelector(state => state.user)
	const logging = useLogging()

	// Close the modal on outside click
	const handleOutsideClick = e => {
		if (e.target.id === "modalOverlay") {
			setModalOpen(false)
		}
	}

	// Close the modal on escape key press
	useEffect(() => {
		const handleEscape = e => {
			if (e.key === "Escape") {
				setModalOpen(false)
			}
		}
		if (modalOpen) {
			window.addEventListener("keydown", handleEscape)
		}
		return () => {
			window.removeEventListener("keydown", handleEscape)
		}
	}, [modalOpen, setModalOpen]) // Only add the listener when modalOpen is true

	const handleSave = async () => {
		try {
			const token = await getAccessTokenSilently()

			// Make a POST request to the backend route to update the user's app_metadata
			const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user`, {
				// Replace with your actual endpoint
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${token}` // Include the user's auth token if needed
				},
				body: JSON.stringify({
					user_metadata: {
						colorScheme: colorScheme,
						viewMode: viewMode,
						select: select,
						columnCount: columnCount
					}
				})
			})

			if (!response.ok) {
				throw new Error("Failed to update user metadata")
			}
		} catch (error) {
			logging.error("Error updating user metadata:", {
				error: error.message,
				stack: error.stack
			})
		}

		setModalOpen(false)
	}

	return (
		<>
			{modalOpen && (
				<div
					id="modalOverlay"
					className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-30"
					onClick={handleOutsideClick}
				>
					<div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
						<h2 className="text-lg font-semibold mb-4 text-gray-900">Customize View</h2>

						{/* Dropdown for "Colored by" */}
						<div className="mb-6 text-left">
							<label className="block mb-2 text-sm font-semibold text-gray-900 text-left">
								Colored by:
							</label>
							<select
								className="w-full h-10 text-sm text-gray-900 rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 text-left"
								value={select}
								onChange={e => dispatch(setSelect(e.target.value))}
							>
								<option value="Acuity">Patient Acuity</option>
								<option value="Duration">Duration</option>
							</select>
						</div>

						{/* Dropdown for Color Scheme / Theme */}
						<div className="mb-6 text-left">
							<label className="block mb-2 text-sm font-semibold text-gray-900 text-left">
								Color Scheme:
							</label>
							<select
								className="w-full h-10 text-sm text-gray-900 rounded-md py-2 px-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 text-left"
								value={colorScheme}
								onChange={e => dispatch(setColorScheme(e.target.value))} // This updates the selected theme
							>
								<option value="default">Default</option>
								<option value="coolBlues">Cool Blues</option>
								<option value="warmSunset">Warm Sunset</option>
								<option value="trafficLights">Traffic Lights</option>
							</select>
						</div>

						{/* Circles to show color preview */}
						<div className="flex space-x-2 mb-6">
							{getThemeColors(colorScheme).map((color, index) => (
								<div key={index} className={`w-8 h-8 rounded-full ${color}`} />
							))}
						</div>

						{/* Radio Buttons for View Mode */}
						<fieldset className="mb-6 text-left">
							<legend className="block mb-2 text-sm font-semibold text-gray-900">
								View Mode:
							</legend>
							<div className="space-y-4 sm:flex sm:items-center sm:space-x-6 sm:space-y-0">
								<div className="flex items-center">
									<input
										id="Detailed"
										name="viewMode"
										type="radio"
										value="Detailed"
										checked={viewMode === "Detailed"}
										onChange={e => dispatch(setViewMode(e.target.value))}
										className="h-4 w-4 border-gray-300 text-customBlue focus:ring-customBlue"
									/>
									<label
										htmlFor="Detailed"
										className="ml-3 block text-sm font-medium text-gray-900"
									>
										Detailed
									</label>
								</div>
								<div className="flex items-center">
									<input
										id="Minimal"
										name="viewMode"
										type="radio"
										value="Minimal"
										checked={viewMode === "Minimal"}
										onChange={e => dispatch(setViewMode(e.target.value))}
										className="h-4 w-4 border-gray-300 text-customBlue focus:ring-customBlue"
									/>
									<label
										htmlFor="Minimal"
										className="ml-3 block text-sm font-medium text-gray-900"
									>
										Minimal
									</label>
								</div>
							</div>
						</fieldset>

						{/* Save Button */}
						<button
							onClick={() => handleSave()}
							className="mt-4 w-full bg-customBlue text-white rounded-md py-2 transition duration-150 hover:bg-customBlue-dark"
						>
							Save Changes
						</button>
					</div>
				</div>
			)}
		</>
	)
}

export default CalendarSettings
